/* Hallmark · component: DropdownMenu · genre: modern-minimal · theme: maeiz-tokens
 * Collision-aware placement (top/right/left/bottom)
 */
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import React from "react";
import { clsx } from "clsx";
import { Check, ChevronRight } from "lucide-react";
import { menuContentMotionClass, overlayCollision } from "../../styles/overlay";

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
export const DropdownMenuSub = DropdownMenuPrimitive.Sub;
export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const contentClass = clsx(
    "z-[80] min-w-[10rem] overflow-hidden rounded-xl border border-neutral-border bg-white p-1 shadow-mz-lg dark:border-white/12 dark:bg-neutral-navy",
    menuContentMotionClass,
);

export const DropdownMenuContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 6, collisionPadding = overlayCollision.collisionPadding, ...props }, ref) => (
    <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            avoidCollisions={overlayCollision.avoidCollisions}
            collisionPadding={collisionPadding}
            className={clsx(contentClass, className)}
            {...props}
        />
    </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = "DropdownMenuContent";

export const DropdownMenuItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & { destructive?: boolean }
>(({ className, destructive, ...props }, ref) => (
    <DropdownMenuPrimitive.Item
        ref={ref}
        className={clsx(
            "relative flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm outline-none",
            "data-[disabled]:pointer-events-none data-[disabled]:opacity-40",
            "data-[highlighted]:bg-neutral-light dark:data-[highlighted]:bg-white/10",
            destructive ? "text-danger" : "text-neutral-dark dark:text-white",
            className,
        )}
        {...props}
    />
));
DropdownMenuItem.displayName = "DropdownMenuItem";

export const DropdownMenuCheckboxItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, ...props }, ref) => (
    <DropdownMenuPrimitive.CheckboxItem
        ref={ref}
        className={clsx(
            "relative flex cursor-pointer select-none items-center rounded-lg py-2 pe-2.5 ps-8 text-sm outline-none",
            "data-[highlighted]:bg-neutral-light dark:data-[highlighted]:bg-white/10",
            className,
        )}
        {...props}
    >
        <span className="absolute start-2.5 flex h-3.5 w-3.5 items-center justify-center">
            <DropdownMenuPrimitive.ItemIndicator>
                <Check size={14} />
            </DropdownMenuPrimitive.ItemIndicator>
        </span>
        {children}
    </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";

export const DropdownMenuLabel = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Label
        ref={ref}
        className={clsx("px-2.5 py-1.5 text-xs font-semibold text-neutral-secondary", className)}
        {...props}
    />
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";

export const DropdownMenuSeparator = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Separator
        ref={ref}
        className={clsx("-mx-1 my-1 h-px bg-neutral-border dark:bg-white/10", className)}
        {...props}
    />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

export const DropdownMenuSubTrigger = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger>
>(({ className, children, ...props }, ref) => (
    <DropdownMenuPrimitive.SubTrigger
        ref={ref}
        className={clsx(
            "flex cursor-pointer select-none items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-sm outline-none data-[highlighted]:bg-neutral-light dark:data-[highlighted]:bg-white/10",
            className,
        )}
        {...props}
    >
        {children}
        <ChevronRight size={14} className="opacity-60" />
    </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName = "DropdownMenuSubTrigger";

export const DropdownMenuSubContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, collisionPadding = overlayCollision.collisionPadding, ...props }, ref) => (
    <DropdownMenuPrimitive.SubContent
        ref={ref}
        avoidCollisions={overlayCollision.avoidCollisions}
        collisionPadding={collisionPadding}
        className={clsx(contentClass, className)}
        {...props}
    />
));
DropdownMenuSubContent.displayName = "DropdownMenuSubContent";
